import { useMemo } from 'react'
import Upload from './Upload'
import MediaLibraryGrid from './MediaLibraryGrid'
import { CubeLoader } from '@/components/ui/cube-loader'

// Transform ad assets to match MediaLibraryGrid expected format
const transformAdAssetsToPhotos = (adAssets) => {
    if (!adAssets || !Array.isArray(adAssets)) return []

    return adAssets.map((asset) => ({
        id: asset.id,
        name: asset.name,
        url: `${import.meta.env.VITE_S3_BUCKET_URL}${asset.url}`,
        width: asset.width,
        height: asset.height,
        type: asset.type,
    }))
}

const MediaLibraryTabContent = ({
    campaignId,
    adId,
    campaignFormat,
    selectedImages,
    handleSelect,
    mediaData,
    adAssets,
    mediaLoading,
    mediaSearchQuery,
    onSearchChange,
    onUploadModeChange,
    onUploadComplete,
    showUpload,
    setShowUpload,
}) => {
    // Combine media library photos and uploaded ad assets with source tracking
    const combinedPhotos = useMemo(() => {
        const mediaLibraryPhotos = mediaData?.Media?.photos || []
        const transformedAdAssets = transformAdAssetsToPhotos(adAssets)

        // Combine both arrays, putting uploaded assets first
        return [...transformedAdAssets, ...mediaLibraryPhotos]
    }, [mediaData, adAssets])

    // Create a map of photo IDs to their sources
    const photoSources = useMemo(() => {
        const sources = {}

        // Mark transformed ad assets as 'upload' source
        const transformedAdAssets = transformAdAssetsToPhotos(adAssets)
        transformedAdAssets.forEach((photo) => {
            sources[photo.id] = 'upload'
        })

        // Mark media library photos as 'meta' source
        const mediaLibraryPhotos = mediaData?.Media?.photos || []
        mediaLibraryPhotos.forEach((photo) => {
            sources[photo.id] = 'meta'
        })

        return sources
    }, [mediaData, adAssets])

    const handleUploadComplete = () => {
        // Ensure proper state synchronization by updating upload mode first
        onUploadModeChange?.(false)
        // Return to media library view after successful upload
        setShowUpload(false)
        // Trigger parent's upload complete callback to refresh data
        onUploadComplete?.()
    }

    const handleCancelUpload = () => {
        setShowUpload(false)
        onUploadModeChange?.(false)
    }

    return (
        <>
            {showUpload ? (
                <Upload
                    adId={adId}
                    campaignId={campaignId}
                    onCancel={handleCancelUpload}
                    campaignFormat={campaignFormat}
                    onUploadComplete={handleUploadComplete}
                />
            ) : (
                <>
                    {mediaLoading && (
                        <div className="tw:w-full tw:flex tw:items-center tw:justify-center">
                            <CubeLoader size="default" fullScreen={true} />
                        </div>
                    )}
                    {!mediaLoading && (
                        <div className="tw:px-6 tw:pt-6">
                            <MediaLibraryGrid
                                loading={mediaLoading}
                                photos={combinedPhotos}
                                photoSources={photoSources}
                                searchQuery={mediaSearchQuery}
                                onSearchChange={onSearchChange}
                                selectedPhotos={selectedImages}
                                onPhotoSelect={(photoId, source, url) =>
                                    handleSelect(campaignFormat === 'carousel', photoId, source, url)
                                }
                            />
                        </div>
                    )}
                </>
            )}
        </>
    )
}

export default MediaLibraryTabContent
